import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Plus } from 'lucide-react';
import API from '@/api/client';
import { formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const TherapistContent = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    media_url: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await API.get('/therapist/content');
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load your content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await API.post('/therapist/content', formData);
      toast.success('Content submitted for review!');
      setShowForm(false);
      setFormData({ title: '', content: '', media_url: '' });
      fetchPosts();
    } catch (error) {
      console.error('Error submitting content:', error);
      toast.error('Failed to submit content');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile Logo"
              className="h-10"
            />
            <h1 className="font-heading text-xl font-bold text-gray-800">My Content</h1>
          </div>
          <Button onClick={() => window.location.href = '/therapist/dashboard'} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
        <PageHeader
          title="Content Submissions"
          subtitle="Share tips and exercises with patients"
          actions={
            <Button onClick={() => setShowForm(!showForm)} data-testid="toggle-form-button">
              <Plus className="w-4 h-4 mr-2" />
              {showForm ? 'Cancel' : 'Submit New Content'}
            </Button>
          }
        />

        {showForm && (
          <Card className="p-6 mb-6" data-testid="content-form">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., 5 Exercises for Lower Back Pain"
                  required
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label>Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Share your tips, exercises, or advice..."
                  rows={6}
                  required
                  data-testid="content-input"
                />
              </div>

              <div>
                <Label>Media URL (Optional)</Label>
                <Input
                  value={formData.media_url}
                  onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="media-url-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Add a link to a video or image to enhance your content
                </p>
              </div>

              <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-content-button">
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </Button>
            </form>
          </Card>
        )}

        {loading ? (
          <LoadingSpinner size="lg" text="Loading your content..." />
        ) : (
          <div className="space-y-4">
            <h2 className="font-heading text-xl font-semibold text-gray-800">
              Your Submissions ({posts.length})
            </h2>
            {posts.map((post, index) => (
              <Card key={post.id} className="p-6" data-testid={`post-card-${index}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <h3 className="font-heading text-lg font-semibold text-gray-800">
                      {post.title}
                    </h3>
                  </div>
                  <StatusBadge status={post.status} />
                </div>

                <p className="text-muted-foreground mb-3">{post.content}</p>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Submitted {formatDateTime(post.created_at)}</span>
                  {post.status === 'approved' && (
                    <span className="text-green-600 font-medium">
                      {post.views_count || 0} views • Earning points!
                    </span>
                  )}
                </div>
              </Card>
            ))}

            {posts.length === 0 && (
              <Card className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-2">
                  No content yet
                </h3>
                <p className="text-muted-foreground mb-4">
                  Share your expertise and earn points when patients view your content
                </p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Post
                </Button>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default TherapistContent;